"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
//import { toast } from "@/hooks/use-toast"
import { AppContext } from "@/App"
import { Button } from "@/Components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Textarea } from "@/Components/ui/textarea"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import DatePicker from "@/Components/ui/DatePicker"

import { Risk } from "@/Components/Risks/Risk";
import React from "react"
import { Checkbox } from "@/Components/ui/checkbox"
import { getRatingCombination } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile";

const formSchema = z.object({
    risk: z
        .string()
        .min(10, {
            message: "Risk must be at least 10 characters.",
        })
        .max(250, {
            message: "Risk must not be longer than 250 characters.",
        }),
    mitigation: z.string()
        .min(10, {
            message: "Mitigation must be at least 10 characters.",
        })
        .max(250, {
            message: "Mitigation must not be longer than 250 characters.",
        }),
    rating: z.string(),
    impact: z.string(),
    probability: z.string(),
    owner: z.string(),
    raisedBy: z.string(),
    workstream: z
        .string({
            required_error: "Please select a workstream.",
        }),
    workPackage: z.string({
        required_error: "Please select a work package",
    }),
    date: z.date(),
    status: z.enum(["Open", "Closed", "Cancelled"]),
    notes: z.string(),
    displayOnDashboard: z.boolean(),
    displayOnStatusReport: z.boolean()
})

interface RiskFormProps {
    risk?: Risk,
    saveRisk: (risk: Risk) => void
}

function RiskForm({ risk, saveRisk }: RiskFormProps) {
    const appContext = React.useContext(AppContext);
    const teamOptions = appContext.projectManagers?.concat(appContext.teamMembers).concat(appContext.sponsors) || [];
    const isMobile = useIsMobile();
    const [riskLength, setRiskLength] = React.useState(risk ? risk.risk.length : 0);
    const [mitigationLength, setMitigationLength] = React.useState(risk ? risk.mitigation.length : 0);
    const [notesLength, setNotesLength] = React.useState(risk ? risk.notes?.length ?? 0 : 0);

    // 1. Define your form.
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            workstream: risk ? risk.workstream : (appContext.selectedProject?.projectTypeId === 1 ? appContext.workPackageOptions?.[0]?.workstreamName : ""),
            workPackage: risk ? risk.workPackage : "",
            owner: risk ? risk.userId === "" ? "unassigned" : risk.userId : "unassigned",
            raisedBy: risk ? risk.raisedBy : "",
            impact: risk ? risk.impact : "Medium",
            probability: risk ? risk.probability : "Medium",
            risk: risk ? risk.risk : "",
            mitigation: risk ? risk.mitigation : "",
            rating: risk ? risk.rating : "Medium",
            date: risk ? new Date(risk.date) : new Date(),
            status: risk ? risk.status : "Open",
            notes: risk ? risk.notes ? risk.notes : "" : "",
            displayOnDashboard: risk ? risk.displayOnDashboard : false,
            displayOnStatusReport: risk ? risk.displayOnStatusReport : false

        },
        mode: "onBlur",
    })

    // 2. Define a submit handler.
    function onSubmit(data: z.infer<typeof formSchema>) {

        const newRisk = {
            projectId: appContext.selectedProject?.id || 0,
            riskId: risk?.riskId || 0,
            workstream: appContext.workPackageOptions?.[0]?.workstreamName || "",
            workstreamId: appContext.workPackageOptions?.[0]?.workstreamId || 0,
            workPackageId: appContext.workPackageOptions?.find((ws) => ws.workstreamName === data.workstream)?.workPackageNames
                .find((wp) => wp.workPackageName === data.workPackage)?.workPackageId || 0,
            workPackage: data.workPackage,
            risk: data.risk,
            mitigation: data.mitigation,
            rating: getRatingCombination(data.probability as "High" | "Medium" | "Low", data.impact as "High" | "Medium" | "Low") as "High" | "Medium" | "Low",
            impact: (["High", "Medium", "Low"].includes(data.impact) ? (data.impact) : "Medium") as "High" | "Medium" | "Low",
            probability: (["High", "Medium", "Low"].includes(data.probability) ? (data.probability) : "Medium") as "High" | "Medium" | "Low",
            owner: teamOptions.find(t => t.id == data.owner)?.displayName ?? "Unassigned",
            userId: data.owner == "unassigned" ? "" : data.owner,
            raisedBy: data.raisedBy,
            date: new Date(data.date),
            status: data.status as "Closed" | "Cancelled" | "Open",
            notes: data.notes,
            displayOnDashboard: data.displayOnDashboard,
            displayOnStatusReport: data.displayOnStatusReport,
            seen: false,
            acknowledged: false,
        }

        if (newRisk.workPackageId == 0) {
            form.setError("workPackage", { type: "manual", message: "Please select a work package" });
            return;
        }

        if (newRisk.workstreamId == 0) {
            form.setError("workstream", { type: "manual", message: "Please select a workstream" });
            return;
        }

        saveRisk(newRisk);

        if (risk == null || risk == undefined) {
            form.setValue("risk", "");
            form.setValue("mitigation", "");
            form.setValue("notes", "");

            setTimeout(() => form.setFocus("risk"), 0);
        }
    }

    if (isMobile) {
        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
                    <FormField
                        control={form.control}
                        name="workPackage"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Work Package</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="text-2xl w-full p-2">
                                            <SelectValue placeholder="Select a work package" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {appContext.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                            <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="probability"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Probability</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl p-2 w-[200px]">
                                                <SelectValue placeholder="Select a Probability" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="impact"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Impact</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl p-2 w-[200px]">
                                                <SelectValue placeholder="Select an Impact" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">
                                        Rating
                                        <span className="ml-1 cursor-pointer" title="Rating is automatically calculated based on Probability and Impact.">
                                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ display: "inline", verticalAlign: "middle" }}>
                                                <circle cx="8" cy="8" r="8" fill="#e2e8f0" />
                                                <text x="8" y="11" textAnchor="middle" fontSize="10" fill="#334155" fontFamily="Arial">?</text>
                                            </svg>
                                        </span>
                                    </FormLabel>
                                    <Select
                                        disabled={true}
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        value={getRatingCombination(
                                            form.watch("probability") as "Low" | "Medium" | "High",
                                            form.watch("impact") as "Low" | "Medium" | "High"
                                        )}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="text-2xl p-2 w-[200px]">
                                                <SelectValue placeholder="Select a Rating" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl w-[425px] p-2">
                                                    <SelectValue placeholder="Raised By..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="text-2xl w-[200px]" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl w-[425px] p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem key={"unassigned"} value={"unassigned"}>Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[200px] p-2">
                                                <SelectValue placeholder="Select a Status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="risk"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Risk</div>
                                    <div><span className="text-lg flex text-gray-500 items-end pr-2">{riskLength}/250</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your risk..."
                                        className="text-2xl resize-none"
                                        rows={4}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setRiskLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="mitigation"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Mitigation</div>
                                    <div><span className="text-lg flex text-gray-500 items-end pr-2">{mitigationLength}/250</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of the mitigation..."
                                        className="text-2xl resize-none"
                                        rows={4}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setMitigationLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Notes</div>
                                    <div><span className="text-lg flex text-gray-500 items-end pr-2">{notesLength}/500</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="text-2xl resize-none"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="displayOnDashboard"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                                    <FormControl className="scale-150 origin-left mr-2">
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="text-2xl !mt-0">Display On Dashboard</FormLabel>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="displayOnStatusReport"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                                    <FormControl className="scale-150 origin-right">
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="text-2xl !mt-0">Display on Status Report</FormLabel>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <Button type="submit" className="w-full text-2xl">Submit</Button>
                </form>
            </Form>
        )
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="workPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Work Package</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a work package" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {appContext.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="probability"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Probability</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a Probability" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="impact"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Impact</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select an Impact" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        Rating
                                        <span className="ml-1 cursor-pointer" title="Rating is automatically calculated based on Probability and Impact.">
                                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ display: "inline", verticalAlign: "middle" }}>
                                                <circle cx="8" cy="8" r="8" fill="#e2e8f0" />
                                                <text x="8" y="11" textAnchor="middle" fontSize="10" fill="#334155" fontFamily="Arial">?</text>
                                            </svg>
                                        </span>
                                    </FormLabel>
                                    <Select
                                        disabled={true}
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        value={getRatingCombination(
                                            form.watch("probability") as "Low" | "Medium" | "High",
                                            form.watch("impact") as "Low" | "Medium" | "High"
                                        )}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a Rating" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a Status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem key={"unassigned"} value={"unassigned"}>Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Raised By..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={form.control}
                        name="risk"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Risk</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{riskLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your risk..."
                                        className="resize-none"
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setRiskLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="mitigation"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Mitigation</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{mitigationLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of the mitigation..."
                                        className="resize-none"
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setMitigationLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Notes </div><div><span className="text-xs flex text-gray-500 items-end pr-2">{notesLength}/500</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row ml-auto space-x-4">
                        <FormField
                            control={form.control}
                            name="displayOnDashboard"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Display On Dashboard</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="displayOnStatusReport"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Display on Status Report</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button type="submit" className="w-48">Submit</Button>
                    </div>
                </div>
            </form>
        </Form>
    )
}

export default RiskForm;